import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Chip, Skeleton, IconButton, Select, MenuItem,
  FormControl, Tooltip, Button,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { keyframes } from '@mui/system';
import { TickerLink } from './TickerActionMenu';
import { bloomberg } from '../theme/bloombergTheme';
import {
  getModelPortfolioEarnings,
  downloadEarningsPdf,
  ModelPortfolioEarningsData,
  UpcomingEarningsEntry,
  RecentEarningsEntry,
  EarningsBeatRate,
} from '../services/api';

const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;
const PAGE_SIZE = 50;

const formatMarketCap = (mc: number): string => {
  if (!mc) return '--';
  if (mc >= 1e12) return `$${(mc / 1e12).toFixed(1)}T`;
  if (mc >= 1e9) return `$${(mc / 1e9).toFixed(1)}B`;
  if (mc >= 1e6) return `$${(mc / 1e6).toFixed(0)}M`;
  return `$${mc.toLocaleString()}`;
};

const formatDate = (iso: string): string => {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

const isToday = (iso: string): boolean => {
  const d = new Date(iso + 'T00:00:00');
  return d.toDateString() === new Date().toDateString();
};

const formatEps = (v: number | null): string => v == null ? '--' : `$${v.toFixed(2)}`;

const formatPct = (v: number | null, showPlus = true): string => {
  if (v == null) return '--';
  return `${v > 0 && showPlus ? '+' : ''}${v.toFixed(1)}%`;
};

const pctColor = (v: number | null): string =>
  v == null ? bloomberg.text.muted : v >= 0 ? '#4caf50' : '#ef5350';

const BeatMissBadge: React.FC<{ beat: boolean | null; surprisePct: number | null }> = ({ beat, surprisePct }) => {
  if (beat === null || beat === undefined) return <span style={{ color: bloomberg.text.muted, fontSize: 11 }}>--</span>;
  const color = beat ? '#4caf50' : '#ef5350';
  const bg = beat ? 'rgba(76,175,80,0.15)' : 'rgba(239,83,80,0.15)';
  const pct = surprisePct != null ? ` ${surprisePct > 0 ? '+' : ''}${surprisePct.toFixed(1)}%` : '';
  const isOutlier = surprisePct != null && Math.abs(surprisePct) > 50;
  const chip = (
    <Chip label={`${beat ? 'BEAT' : 'MISS'}${pct}${isOutlier ? ' ⚠' : ''}`} size="small" sx={{
      height: 18, fontSize: 9, fontWeight: 700, bgcolor: bg, color, borderRadius: '4px',
      '& .MuiChip-label': { px: 0.8 },
    }} />
  );
  if (isOutlier) {
    return (
      <Tooltip title="Large surprise — GAAP EPS may include one-time items (spin-offs, investment gains/losses) not reflected in analyst estimates." arrow>
        {chip}
      </Tooltip>
    );
  }
  return chip;
};

const BeatRateDisplay: React.FC<{ rate: EarningsBeatRate | null }> = ({ rate }) => {
  if (!rate) return <span style={{ color: bloomberg.text.muted, fontSize: 11 }}>--</span>;
  const pct = rate.total > 0 ? rate.beats / rate.total : 0;
  const color = pct >= 0.75 ? '#4caf50' : pct >= 0.5 ? bloomberg.accent.orange : '#ef5350';
  return (
    <Tooltip title={`Beat estimates ${rate.beats} out of ${rate.total} recent quarters`}>
      <span style={{ color, fontSize: 12, fontWeight: 700, cursor: 'default' }}>{rate.rate_str}</span>
    </Tooltip>
  );
};

const ModelPortfolioEarnings: React.FC = () => {
  const [data, setData] = useState<ModelPortfolioEarningsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(14);
  const [upcomingPage, setUpcomingPage] = useState(1);
  const [recentPage, setRecentPage] = useState(1);

  const fetchData = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const daysBack = days === 1 ? 0 : days === 0 ? 30 : days;
      const result = await getModelPortfolioEarnings(days, daysBack, isRefresh);
      setData(result);
      setUpcomingPage(1);
      setRecentPage(1);
    } catch (err: any) {
      console.error('Error fetching portfolio earnings:', err);
      setError(err?.response?.data?.detail || err?.message || 'Failed to load earnings data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const allUpcoming = data?.upcoming || [];
  const allRecent = data?.recent || [];
  const totalCount = allUpcoming.length + allRecent.length;

  // Group upcoming by date
  const upcomingGrouped: Record<string, UpcomingEarningsEntry[]> = {};
  allUpcoming.forEach(e => {
    if (!upcomingGrouped[e.earnings_date]) upcomingGrouped[e.earnings_date] = [];
    upcomingGrouped[e.earnings_date].push(e);
  });
  const upcomingSortedDates = Object.keys(upcomingGrouped).sort();

  // Pagination
  const upcomingTotalPages = Math.ceil(allUpcoming.length / PAGE_SIZE);
  const upcomingPageSlice = new Set(
    allUpcoming.slice((upcomingPage - 1) * PAGE_SIZE, upcomingPage * PAGE_SIZE).map(e => e.symbol)
  );
  const upcomingDatesForPage = upcomingSortedDates.filter(d =>
    upcomingGrouped[d].some(e => upcomingPageSlice.has(e.symbol))
  );
  const recentTotalPages = Math.ceil(allRecent.length / PAGE_SIZE);
  const recentPageSlice = allRecent.slice((recentPage - 1) * PAGE_SIZE, recentPage * PAGE_SIZE);

  const thSx = {
    textAlign: 'left' as const, py: 0.8, px: 1, fontSize: 11, fontWeight: 600,
    color: bloomberg.accent.orange, textTransform: 'uppercase' as const, letterSpacing: '0.05em',
  };
  const thSxRight = { ...thSx, textAlign: 'right' as const };
  const tdSx = { py: 0.6, px: 1, fontSize: 12, color: bloomberg.text.secondary };

  return (
    <Box>
      <Paper elevation={2} sx={{ p: 2.5 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarMonthIcon sx={{ color: bloomberg.accent.orange, fontSize: 20 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: bloomberg.accent.orange }}>
              Portfolio Universe Earnings
            </Typography>
            {data && data.universe_count > 0 && (
              <Chip label={`${data.universe_count} stocks in universe`} size="small" sx={{
                height: 20, fontSize: 10, fontWeight: 600,
                bgcolor: bloomberg.accent.blueDim, color: bloomberg.accent.cyan,
              }} />
            )}
            {allRecent.length > 0 && (
              <Chip label={`${allRecent.length} recent`} size="small" sx={{
                height: 20, fontSize: 10, fontWeight: 600,
                bgcolor: 'rgba(76,175,80,0.12)', color: '#4caf50',
              }} />
            )}
            {allUpcoming.length > 0 && (
              <Chip label={`${allUpcoming.length} upcoming`} size="small" sx={{
                height: 20, fontSize: 10, fontWeight: 600,
                bgcolor: bloomberg.accent.orangeDim, color: bloomberg.accent.orange,
              }} />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 90 }}>
              <Select
                value={days}
                onChange={(e) => setDays(e.target.value as number)}
                sx={{
                  height: 28, fontSize: 11, color: bloomberg.text.secondary,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: bloomberg.border.primary },
                  '& .MuiSvgIcon-root': { color: bloomberg.text.muted, fontSize: 16 },
                }}
              >
                <MenuItem value={1}>1 day</MenuItem>
                <MenuItem value={7}>7 days</MenuItem>
                <MenuItem value={14}>14 days</MenuItem>
                <MenuItem value={30}>30 days</MenuItem>
                <MenuItem value={60}>60 days</MenuItem>
                <MenuItem value={0}>All upcoming</MenuItem>
              </Select>
            </FormControl>
            <Tooltip title="Download Weekly Earnings PDF">
              <span>
                <IconButton
                  onClick={async () => {
                    setPdfLoading(true);
                    try { await downloadEarningsPdf('portfolio', 7, 7); }
                    catch (e) { console.error(e); }
                    finally { setPdfLoading(false); }
                  }}
                  disabled={pdfLoading}
                  size="small"
                  sx={{ color: bloomberg.text.muted }}
                >
                  <PictureAsPdfIcon sx={{ fontSize: 18, ...(pdfLoading ? { animation: `${spin} 1s linear infinite` } : {}) }} />
                </IconButton>
              </span>
            </Tooltip>
            <IconButton onClick={() => fetchData(true)} disabled={refreshing} size="small"
              sx={{ color: bloomberg.text.muted }}>
              <RefreshIcon sx={{ fontSize: 18, ...(refreshing ? { animation: `${spin} 1s linear infinite` } : {}) }} />
            </IconButton>
          </Box>
        </Box>

        {error && !loading && (
          <Box sx={{ p: 1.5, mb: 2, bgcolor: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.35)', borderRadius: 1 }}>
            <Typography sx={{ fontSize: 12, color: '#f59e0b' }}>
              Failed to load earnings data: {error}. Click refresh to retry.
            </Typography>
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {[...Array(8)].map((_, i) => <Skeleton key={i} variant="rounded" height={32} />)}
          </Box>
        ) : !error && totalCount === 0 ? (
          <Typography color="text.secondary" variant="body2" sx={{ py: 3, textAlign: 'center' }}>
            No earnings found for portfolio stocks in the selected range.
          </Typography>
        ) : (
          <>
            {/* ===== RECENT RESULTS ===== */}
            {allRecent.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#4caf50', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Recent Results ({allRecent.length})
                  </Typography>
                  {recentTotalPages > 1 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Button size="small" disabled={recentPage === 1} onClick={() => setRecentPage(p => p - 1)}
                        sx={{ minWidth: 28, px: 0.5, fontSize: 11, color: bloomberg.text.secondary }}>‹</Button>
                      <Typography sx={{ fontSize: 11, color: bloomberg.text.muted }}>{recentPage}/{recentTotalPages}</Typography>
                      <Button size="small" disabled={recentPage === recentTotalPages} onClick={() => setRecentPage(p => p + 1)}
                        sx={{ minWidth: 28, px: 0.5, fontSize: 11, color: bloomberg.text.secondary }}>›</Button>
                    </Box>
                  )}
                </Box>
                <Box sx={{ overflowX: 'auto' }}>
                  <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
                    <Box component="thead">
                      <Box component="tr" sx={{ borderBottom: `2px solid #4caf50`, bgcolor: bloomberg.bg.secondary }}>
                        <Box component="th" sx={thSx}>Symbol</Box>
                        <Box component="th" sx={thSx}>Company</Box>
                        <Box component="th" sx={thSx}>Date</Box>
                        <Box component="th" sx={thSx}>EPS Est / Act</Box>
                        <Box component="th" sx={thSx}>Beat/Miss</Box>
                        <Box component="th" sx={thSxRight}>Post-Earn Move</Box>
                        <Box component="th" sx={{ ...thSx, textAlign: 'center' as const }}>Beat Rate</Box>
                        <Box component="th" sx={thSxRight}>Mkt Cap</Box>
                      </Box>
                    </Box>
                    <Box component="tbody">
                      {recentPageSlice.map((e: RecentEarningsEntry) => (
                        <Box component="tr" key={e.symbol} sx={{
                          borderBottom: `1px solid ${bloomberg.border.primary}`,
                          '&:hover': { bgcolor: bloomberg.bg.hover },
                        }}>
                          <Box component="td" sx={tdSx}><TickerLink symbol={e.symbol} fontSize={12} /></Box>
                          <Box component="td" sx={{ ...tdSx, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.company_name}</Box>
                          <Box component="td" sx={{ ...tdSx, fontSize: 11, color: bloomberg.text.muted }}>{formatDate(e.earnings_date)}</Box>
                          <Box component="td" sx={tdSx}>
                            {e.revenue_only ? (
                              <span style={{ color: bloomberg.text.muted, fontStyle: 'italic', fontSize: 11 }}>Revenue only</span>
                            ) : (
                              <>
                                <span style={{ color: bloomberg.text.muted }}>{formatEps(e.eps_estimate)}</span>
                                <span style={{ color: bloomberg.text.muted, margin: '0 3px' }}>/</span>
                                <Tooltip title="GAAP EPS — may differ from adjusted estimates due to one-time items" arrow>
                                  <span style={{ fontWeight: 600, color: e.beat_eps ? '#4caf50' : e.beat_eps === false ? '#ef5350' : bloomberg.text.secondary }}>
                                    {formatEps(e.eps_actual)}
                                  </span>
                                </Tooltip>
                              </>
                            )}
                          </Box>
                          <Box component="td" sx={tdSx}>{!e.revenue_only && <BeatMissBadge beat={e.beat_eps} surprisePct={e.eps_surprise_pct} />}</Box>
                          <Box component="td" sx={{ ...tdSx, textAlign: 'right', fontWeight: 600, color: pctColor(e.post_earnings_move) }}>
                            {formatPct(e.post_earnings_move)}
                          </Box>
                          <Box component="td" sx={{ ...tdSx, textAlign: 'center' }}><BeatRateDisplay rate={e.beat_rate} /></Box>
                          <Box component="td" sx={{ ...tdSx, textAlign: 'right', fontWeight: 600 }}>{formatMarketCap(e.market_cap)}</Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}

            {/* ===== UPCOMING EARNINGS ===== */}
            {allUpcoming.length > 0 && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: bloomberg.accent.orange, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Upcoming Earnings ({allUpcoming.length})
                  </Typography>
                  {upcomingTotalPages > 1 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Button size="small" disabled={upcomingPage === 1} onClick={() => setUpcomingPage(p => p - 1)}
                        sx={{ minWidth: 28, px: 0.5, fontSize: 11, color: bloomberg.text.secondary }}>‹</Button>
                      <Typography sx={{ fontSize: 11, color: bloomberg.text.muted }}>
                        {upcomingPage}/{upcomingTotalPages} · {((upcomingPage - 1) * PAGE_SIZE) + 1}–{Math.min(upcomingPage * PAGE_SIZE, allUpcoming.length)} of {allUpcoming.length}
                      </Typography>
                      <Button size="small" disabled={upcomingPage === upcomingTotalPages} onClick={() => setUpcomingPage(p => p + 1)}
                        sx={{ minWidth: 28, px: 0.5, fontSize: 11, color: bloomberg.text.secondary }}>›</Button>
                    </Box>
                  )}
                </Box>
                <Box sx={{ overflowX: 'auto' }}>
                  <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
                    <Box component="thead">
                      <Box component="tr" sx={{ borderBottom: `2px solid ${bloomberg.accent.orange}`, bgcolor: bloomberg.bg.secondary }}>
                        <Box component="th" sx={thSx}>Symbol</Box>
                        <Box component="th" sx={thSx}>Company</Box>
                        <Box component="th" sx={thSx}>Sector</Box>
                        <Box component="th" sx={thSxRight}>EPS Estimate</Box>
                        <Box component="th" sx={{ ...thSx, textAlign: 'center' as const }}>Beat Rate</Box>
                        <Box component="th" sx={thSxRight}>Mkt Cap</Box>
                      </Box>
                    </Box>
                    <Box component="tbody">
                      {upcomingDatesForPage.map(dateStr => (
                        <React.Fragment key={dateStr}>
                          <Box component="tr">
                            <Box component="td" colSpan={6} sx={{
                              py: 0.5, px: 1,
                              bgcolor: isToday(dateStr) ? bloomberg.accent.orangeDim : bloomberg.bg.tertiary,
                              borderLeft: isToday(dateStr) ? `3px solid ${bloomberg.accent.orange}` : `3px solid ${bloomberg.border.secondary}`,
                            }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: isToday(dateStr) ? bloomberg.accent.orange : bloomberg.text.primary }}>
                                  {formatDate(dateStr)}
                                </Typography>
                                {isToday(dateStr) && (
                                  <Chip label="TODAY" size="small" sx={{ height: 16, fontSize: 9, fontWeight: 800, bgcolor: bloomberg.accent.orange, color: '#000' }} />
                                )}
                                <Chip label={`${upcomingGrouped[dateStr].length}`} size="small" sx={{ height: 16, fontSize: 9, fontWeight: 700, bgcolor: bloomberg.accent.blueDim, color: bloomberg.accent.cyan }} />
                              </Box>
                            </Box>
                          </Box>
                          {upcomingGrouped[dateStr].filter(e => upcomingPageSlice.has(e.symbol)).map((e: UpcomingEarningsEntry) => (
                            <Box component="tr" key={e.symbol} sx={{
                              borderBottom: `1px solid ${bloomberg.border.primary}`,
                              '&:hover': { bgcolor: bloomberg.bg.hover },
                            }}>
                              <Box component="td" sx={tdSx}><TickerLink symbol={e.symbol} fontSize={12} /></Box>
                              <Box component="td" sx={{ ...tdSx, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.company_name}</Box>
                              <Box component="td" sx={{ ...tdSx, fontSize: 11, color: bloomberg.text.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.sector || '--'}</Box>
                              <Box component="td" sx={{ ...tdSx, textAlign: 'right', fontWeight: 600 }}>{formatEps(e.eps_estimate)}</Box>
                              <Box component="td" sx={{ ...tdSx, textAlign: 'center' }}><BeatRateDisplay rate={e.beat_rate} /></Box>
                              <Box component="td" sx={{ ...tdSx, textAlign: 'right', fontWeight: 600 }}>{formatMarketCap(e.market_cap)}</Box>
                            </Box>
                          ))}
                        </React.Fragment>
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default ModelPortfolioEarnings;
